import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Close, ShoppingCart } from '@mui/icons-material';
import { getCreditProducts, CreditProduct, purchaseCreditProduct } from '../../services/billing.service';
import { toast } from 'react-toastify';

interface CreditPurchaseDialogProps {
    open: boolean;
    onClose: () => void;
    onPurchase: (product: CreditProduct) => void;
    userId?: string;
}

export default function CreditPurchaseDialog({ open, onClose, onPurchase, userId }: CreditPurchaseDialogProps) {
    const [loading, setLoading] = useState(false);
    const [creditProducts, setCreditProducts] = useState<CreditProduct[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [purchasing, setPurchasing] = useState<string | null>(null);

    // 다이얼로그가 열릴 때 크레딧 상품 목록 가져오기
    useEffect(() => {
        if (open) {
            fetchCreditProducts();
        }
    }, [open]);

    const fetchCreditProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getCreditProducts();

            // API 응답 구조 확인 및 데이터 추출
            let products: CreditProduct[] = [];
            if (response && response.data && Array.isArray(response.data)) {
                products = response.data as CreditProduct[];
            } else if (response && Array.isArray(response)) {
                products = response as CreditProduct[];
            } else {
                throw new Error('예상하지 못한 API 응답 구조입니다.');
            }

            // fittingSubscriptionPlans가 비어있는 상품만 필터링
            const creditOnlyProducts = products.filter((product) => {
                const hasNoSubscriptionPlans =
                    product.fittingSubscriptionPlans && product.fittingSubscriptionPlans.length === 0;
                return hasNoSubscriptionPlans;
            });

            setCreditProducts(creditOnlyProducts);
        } catch (err) {
            console.error('크레딧 상품을 가져오는데 실패했습니다:', err);
            setError('크레딧 상품을 불러오는데 실패했습니다. 다시 시도해주세요.');
            toast.error('크레딧 상품을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = async (product: CreditProduct) => {
        if (!userId) {
            toast.error('사용자 정보를 찾을 수 없습니다.');
            return;
        }

        try {
            setPurchasing(product.id);

            const response = await purchaseCreditProduct(userId, product.id);
            toast.success(`구매가 완료되었습니다.`);
            onPurchase(product);
            onClose();
        } catch (error: any) {
            if (error.status === 400) {
                toast.error(error?.data?.placeHolder || '구매에 실패했습니다. 다시 시도해주세요.');
            }
        } finally {
            setPurchasing(null);
        }
    };

    const formatPrice = (price: number) => {
        return price.toLocaleString('ko-KR');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 3,
                    pt: 3,
                    px: 3,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: '#1F2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '24px',
                    }}
                >
                    크레딧 추가구매
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{
                        minWidth: 'auto',
                        p: 0.5,
                        color: '#6B7280',
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                        },
                    }}
                >
                    <Close sx={{ fontSize: '20px' }} />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 3, pt: 4, px: 3 }}>
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 8,
                        }}
                    >
                        <CircularProgress size={40} />
                        <Typography sx={{ ml: 2, color: '#6B7280' }}>크레딧 상품을 불러오는 중...</Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : creditProducts.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 8,
                        }}
                    >
                        <Typography sx={{ color: '#6B7280', fontSize: '16px' }}>
                            구매 가능한 크레딧 상품이 없습니다.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                            },
                            gap: 3,
                        }}
                    >
                        {creditProducts.map((product) => (
                            <Box key={product.id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: 2,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            borderColor: '#374151',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                        },
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        {/* 첫 번째 줄: 1개당 가격과 상품명 */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#1F2937',
                                                    fontSize: '18px',
                                                }}
                                            >
                                                {product.name}
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: '#9CA3AF',
                                                        fontWeight: 400,
                                                        ml: 1,
                                                    }}
                                                >
                                                    (1개당 ₩
                                                    {formatPrice(
                                                        Math.round(product.totalCharge / product.creditAmount),
                                                    )}
                                                    )
                                                </Box>
                                            </Typography>
                                        </Box>

                                        {/* 가격 정보 */}
                                        <Box sx={{ mb: 3 }}>
                                            {product.discountAmount > 0 ? (
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: '#6B7280',
                                                            textDecoration: 'line-through',
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        ₩{formatPrice(product.originalPrice)}
                                                    </Typography>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: '#DC2626',
                                                            fontSize: '24px',
                                                        }}
                                                    >
                                                        ₩{formatPrice(product.totalCharge)}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '12px',
                                                            color: '#DC2626',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {formatPrice(product.discountAmount)}원 할인
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: '#1F2937',
                                                        fontSize: '24px',
                                                    }}
                                                >
                                                    ₩{formatPrice(product.totalCharge)}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* 설명 */}
                                        {product.description && (
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: '#6B7280',
                                                    mb: 2,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {product.description}
                                            </Typography>
                                        )}
                                    </CardContent>

                                    {/* 구매 버튼 */}
                                    <Box sx={{ p: 3, pt: 0 }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => handlePurchase(product)}
                                            disabled={purchasing === product.id}
                                            sx={{
                                                backgroundColor: '#374151',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                py: 1.5,
                                                borderRadius: 2,
                                                fontSize: '16px',
                                                '&:hover': {
                                                    backgroundColor: '#1F2937',
                                                },
                                                '&:disabled': {
                                                    backgroundColor: '#9CA3AF',
                                                    color: 'white',
                                                },
                                            }}
                                        >
                                            {purchasing === product.id ? (
                                                <>
                                                    <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                                    구매 중...
                                                </>
                                            ) : (
                                                '구매하기'
                                            )}
                                        </Button>
                                    </Box>
                                </Card>
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
